/* @jsx h */
import { defineComponent, h } from "vue";
import { OrderDiscountReward } from "@lemmonhq/types";

export type OrderDiscountRewardEditorProps = {
  value: OrderDiscountReward;
  onChange: (reward: OrderDiscountReward) => void;
};

export const OrderDiscountRewardEditor =
  defineComponent<OrderDiscountRewardEditorProps>({
    name: "OrderDiscountRewardEditor",
    props: ["value", "onChange"],
    setup(props) {
      const updateField = (field: keyof OrderDiscountReward, value: any) => {
        props.onChange({
          ...props.value,
          [field]: value,
        });
      };

      const formatPreview = () => {
        if (props.value.mode === "percentage") {
          return `${props.value.value}% off entire order`;
        } else {
          return `$${props.value.value} off entire order`;
        }
      };

      return () => (
        <div class="card border bg-base-200">
          <div class="card-body p-4">
            <h4 class="card-title text-accent text-lg flex items-center gap-2">
              <div class="w-3 h-3 bg-warning rounded-full animate-pulse"></div>
              Order Discount Reward
            </h4>

            <div class="space-y-4 mt-4">
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Discount Mode</span>
                </label>

                <div class="grid grid-cols-2 gap-3">
                  <label
                    class={`
                  label cursor-pointer p-4 rounded-lg border-2 transition-all
                  ${
                    props.value.mode === "percentage"
                      ? "border-warning bg-warning/10 shadow-md"
                      : " bg-base-100 hover:border-warning/50"
                  }
                `}
                  >
                    <div class="flex items-center space-x-3 w-full">
                      <input
                        type="radio"
                        name="orderDiscountMode"
                        value="percentage"
                        checked={props.value.mode === "percentage"}
                        onChange={(e) =>
                          updateField(
                            "mode",
                            (e.target as HTMLInputElement).value,
                          )
                        }
                        class="radio radio-warning radio-sm"
                      />
                      <div class="flex-1">
                        <div class="font-medium">Percentage</div>
                        <div class="text-xs text-base-content/70">
                          % off total order
                        </div>
                      </div>
                      <div class="text-2xl">%</div>
                    </div>
                  </label>

                  <label
                    class={`
                  label cursor-pointer p-4 rounded-lg border-2 transition-all
                  ${
                    props.value.mode === "absolute"
                      ? "border-warning bg-warning/10 shadow-md"
                      : " bg-base-100 hover:border-warning/50"
                  }
                `}
                  >
                    <div class="flex items-center space-x-3 w-full">
                      <input
                        type="radio"
                        name="orderDiscountMode"
                        value="absolute"
                        checked={props.value.mode === "absolute"}
                        onChange={(e) =>
                          updateField(
                            "mode",
                            (e.target as HTMLInputElement).value,
                          )
                        }
                        class="radio radio-warning radio-sm"
                      />
                      <div class="flex-1">
                        <div class="font-medium">Fixed Amount</div>
                        <div class="text-xs text-base-content/70">
                          $ off total order
                        </div>
                      </div>
                      <div class="text-2xl">$</div>
                    </div>
                  </label>
                </div>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">
                    {props.value.mode === "percentage"
                      ? "Discount Percentage"
                      : "Discount Amount"}
                  </span>
                </label>

                <div class="relative">
                  <input
                    type="number"
                    value={props.value.value}
                    onInput={(e) =>
                      updateField(
                        "value",
                        parseFloat((e.target as HTMLInputElement).value) || 0,
                      )
                    }
                    class="input input-bordered focus:input-warning w-full"
                    min="0"
                    max={props.value.mode === "percentage" ? 100 : undefined}
                    step={props.value.mode === "percentage" ? 1 : 0.01}
                    placeholder={
                      props.value.mode === "percentage" ? "15" : "10.00"
                    }
                  />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-base-content/50">
                    {props.value.mode === "percentage" ? "%" : "$"}
                  </div>
                </div>

                <label class="label">
                  <span class="label-text-alt">
                    {props.value.mode === "percentage"
                      ? "Enter percentage (1-100)"
                      : "Enter dollar amount"}
                  </span>
                </label>
              </div>
            </div>

            <div class="alert alert-warning/20 border-warning/30 mt-4">
              <div class="flex items-center gap-2">
                <div class="text-warning text-xl">🛒</div>
                <div>
                  <div class="font-medium text-warning">
                    Order Discount Preview
                  </div>
                  <div class="text-sm text-warning/80">
                    Customer will get {formatPreview()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    },
  });
